import type { Middleware } from "openapi-fetch";

import { env } from "@/lib/env";
import { AUTH_EVENTS } from "@/lib/events/constants";
import { emitEvent } from "@/lib/events/helpers";

import { ForbiddenApiError } from "./error.forbidden";
import { InternalServerApiError } from "./error.internal-server";
import { UnauthorizedApiError } from "./error.unauthorized";
import { HTTP_STATUS } from "./http-status";

const APP_INTEGRATIONS_SETTINGS_REGEX = /^\/app-integrations\/[^/]+\/settings$/;

/**
 * トークンプロバイダーの型定義
 */
export interface TokenProvider {
  getAccessToken: () => Promise<string | null>;
}

/**
 * 認証ミドルウェアファクトリー関数
 * NOTE: トークンプロバイダーを外部から注入することで循環参照を回避
 */
export const createAuthMiddleware = (
  tokenProvider?: TokenProvider,
): Middleware => ({
  async onRequest({ request }) {
    let token: string | undefined = env.VITE_API_BEARER_TOKEN ?? undefined;

    if (!token && tokenProvider) {
      const providedToken = await tokenProvider.getAccessToken();
      token = providedToken ?? undefined;
    }

    if (token) {
      request.headers.set("Authorization", `Bearer ${token}`);
    }

    return request;
  },

  async onResponse({ response }) {
    if (
      response.status !== HTTP_STATUS.UNAUTHORIZED &&
      response.status !== HTTP_STATUS.FORBIDDEN
    ) {
      return response;
    }
    if (response.status === HTTP_STATUS.FORBIDDEN) {
      const data = await response.json();
      if (data.accessibles) {
        throw new ForbiddenApiError(data.accessibles);
      }
      // 意図していない403エラーが発生した場合は、InternalServerApiErrorをthrowする
      throw new InternalServerApiError();
    }
    if (response.status === HTTP_STATUS.UNAUTHORIZED) {
      /**
       * NOTE: マーケットプレイスで401が発生してもハンドリングしない。
       * インテグレーション先の認可サーバーが返却した401コードとの区別をつけられないため。
       */
      // router依存を削除し、現在のパスを判定するためのイベントを使用
      const currentPath =
        typeof window !== "undefined" ? window.location.pathname : "";

      if (
        // OAuthの認証で401でもログアウトしない
        currentPath.startsWith("/apps/marketplace") ||
        currentPath.startsWith("/software/marketplace") ||
        // アプリの再認証で401でもログアウトしない（/app-integrations/:id/settings）
        APP_INTEGRATIONS_SETTINGS_REGEX.test(currentPath)
      ) {
        return response;
      }

      // 以下、401の場合のハンドリング
      // 直接logoutを呼ぶ代わりに、イベントを発火してauth systemに委ねる
      emitEvent(AUTH_EVENTS.UNAUTHORIZED, {
        response,
      });

      // logout処理はauth storeで実行されるはずなので、直接呼び出しはコメントアウト
      // useAuthStore.getState().logout();
      throw new UnauthorizedApiError();
    }
    // 予期せぬステータス
    throw new InternalServerApiError();
  },
});

// 従来のAPIとの互換性のため（廃止予定）
// @deprecated 代わりに createAuthMiddleware を使用してください
const authMiddleware: Middleware = createAuthMiddleware();

export default authMiddleware;
